import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import "./taskmanagement.css";
import "./taskTracker.css";
import { confirmDialog, escapeHtml, resultDialog } from "../../utils/swalDialogs";
import AddTaskModal from "../dashboard/AddTaskModal";
import TaskChatPanel, { MentionDot, hasUnseenMention, withMentionsSeen } from "../dashboard/TaskChatPanel";
import {
  addSubtask,
  addOrUpdateMusicTaskAttachments,
  addOrUpdateSportsTaskAttachments,
  createMusicTask,
  createSportsTask,
  createTask,
  deleteAttachemnets,
  fetchUsers,
  getAssignableTaskUsers,
  getBalagruha,
  getBalagruhaById,
  getSportsTaskListByBalagruha,
  getTaskAttachmentBlob,
  getTaskBytaskId,
  getTasks,
  updateMusicTask,
  updateSportsTask,
  updateTask,
  updateTaskAttachments,
  getAnyUserBasedonRoleandBalagruha,
} from "../../api";
import SearchableSelect from "../common/SearchableSelect";
// import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const capitalize = (str) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

// Toast Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`}>
      <div className="toast-content">
        <span className="toast-icon">
          {type === "success"
            ? "✅"
            : type === "error"
              ? "❌"
              : type === "info"
                ? "ℹ️"
                : "🔔"}
        </span>
        <span className="toast-message">{message}</span>
      </div>
      <button className="toast-close" onClick={onClose}>
        ×
      </button>
    </div>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

// File Preview Component
const FilePreview = ({ file }) => {
  const isImage = file.type.startsWith("image/");
  const isPDF = file.type === "application/pdf";

  return (
    <div className="file-preview-item">
      {isImage ? (
        <div className="image-preview">
          <img src={URL.createObjectURL(file)} alt={file.name} />
        </div>
      ) : isPDF ? (
        <div className="pdf-preview">
          <div className="pdf-icon">📄</div>
          <div className="pdf-name">{file.name}</div>
        </div>
      ) : (
        <div className="generic-file-preview">
          <div className="file-icon">📎</div>
          <div className="file-name">{file.name}</div>
        </div>
      )}
      <div className="file-info">
        <div className="file-name">{file.name}</div>
        <div className="file-size">{(file.size / 1024).toFixed(1)} KB</div>
      </div>
    </div>
  );
};

// Comment Component
// Create Task Form Component
export const CreateTaskForm = ({
  users,
  staffUsers = [],
  studentUsers = [],
  balagruhas = [],
  onSubmit,
  onCancel,
  balagruhaId,
}) => {

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "medium",
    deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      .toISOString().slice(0, 16), // Default to 1 week from now
    assignedUser: [],
    status: "pending",
    balagruhaId: balagruhaId || "",
    attachments: [],
    drillOrExerciseType: "",
    machineDetails: "",
    vendorDetails: "",
    costEstimate: "",
    requiredParts: "",
    repairDetails: "",
    type: "general",
    // Medical task specific fields
    medicalBalagruhaId: "",
    students: [],
  });
  const [files, setFiles] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const fileInputRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const role = localStorage.getItem("role");

  // Medical task specific state
  const [availableBalagruhas, setAvailableBalagruhas] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [availableMedicalManagers, setAvailableMedicalManagers] = useState([]);
  const [isBalagruhaDropdownOpen, setIsBalagruhaDropdownOpen] = useState(false);
  const [isStudentDropdownOpen, setIsStudentDropdownOpen] = useState(false);
  const [isAssignToDropdownOpen, setIsAssignToDropdownOpen] = useState(false);
  const [assignToSearchQuery, setAssignToSearchQuery] = useState("");
  const [assignSearchQuery, setAssignSearchQuery] = useState("");
  const balagruhaDropdownRef = useRef(null);
  const studentDropdownRef = useRef(null);
  const assignToDropdownRef = useRef(null);
  const assignableStaff = staffUsers || [];
  const assignableStudents = studentUsers || [];
  const allAssignableUsers = useMemo(
    () => [...assignableStaff, ...assignableStudents],
    [assignableStaff, assignableStudents]
  );

  const normalizedAssignSearch = assignSearchQuery.trim().toLowerCase();


  const selectedBalagruhaId = formData.balagruhaId;

  const isUserInSelectedBalagruha = (user) => {
    if (!selectedBalagruhaId) return true;

    const ids = user.balagruhaIds || user.balagruhaId || [];

    if (Array.isArray(ids)) {
      return ids.some((id) => id?.toString() === selectedBalagruhaId?.toString());
    }

    return ids?.toString() === selectedBalagruhaId?.toString();
  };
  const filteredStaffUsers = useMemo(() => {
    const usersInBalagruha = assignableStaff.filter(isUserInSelectedBalagruha);

    if (!normalizedAssignSearch) {
      return usersInBalagruha;
    }

    return usersInBalagruha.filter((user) => {
      const name = user.name?.toLowerCase() || "";
      const roleLabel = user.role?.toLowerCase() || "";

      return (
        name.includes(normalizedAssignSearch) ||
        roleLabel.includes(normalizedAssignSearch)
      );
    });
  }, [assignableStaff, normalizedAssignSearch, selectedBalagruhaId]);

  const filteredStudentUsers = useMemo(() => {
    const usersInBalagruha = assignableStudents.filter(isUserInSelectedBalagruha);

    if (!normalizedAssignSearch) {
      return usersInBalagruha;
    }

    return usersInBalagruha.filter((user) => {
      const name = user.name?.toLowerCase() || "";
      const roleLabel = user.role?.toLowerCase() || "";

      return (
        name.includes(normalizedAssignSearch) ||
        roleLabel.includes(normalizedAssignSearch)
      );
    });
  }, [assignableStudents, normalizedAssignSearch, selectedBalagruhaId]);

  const initialType =
    role === "sports-coach"
      ? "sports"
      : role === "music-coach"
        ? "music"
        : role === "medical-manager" || role === "medical-incharge"
          ? "medical"
          : "general";

  const [type, setType] = useState(initialType);
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const typeDropdownRef = useRef(null);
  const taskTypes = [
    { id: "sports", label: "🏃‍♂️ Sports Tasks" },
    { id: "music", label: "🎵 Music Tasks" },
    { id: "general", label: "📝 General Tasks" },
    { id: "purchase", label: "🛒 Purchase Tasks" },
    { id: "repair", label: "🔧 Repair Tasks" },
    { id: "medical", label: "🏥 Medical Tasks" },
  ];

  const assignedUserLabel = useMemo(() => {
    if (!formData.assignedUser || formData.assignedUser.length === 0) {
      return "Select Users";
    }
    return formData.assignedUser
      .map(
        (id) => allAssignableUsers.find((user) => user._id === id)?.name || "Unknown User"
      )
      .join(", ");
  }, [formData.assignedUser, allAssignableUsers]);

  const primaryAssigneeId = Array.isArray(formData.assignedUser)
    ? formData.assignedUser[0] || ""
    : formData.assignedUser || "";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        typeDropdownRef.current &&
        !typeDropdownRef.current.contains(event.target)
      ) {
        setIsTypeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const getSelectedTypeLabel = () => {
    const selectedType = taskTypes.find((t) => t.id === type);
    return selectedType ? selectedType.label : "Select Type";
  };

  // Handle type selection
  const handleTypeSelect = (selectedType) => {
    setType(selectedType);
    setIsTypeDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const handleCheckboxChange = (e) => {
    const { checked, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      assignedUser: checked
        ? [...(prev.assignedUser || []), value]
        : (prev.assignedUser || []).filter((id) => id !== value),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "balagruhaId" ? { assignedUser: [] } : {}),
    }));

    if (name === "balagruhaId") {
      setAssignSearchQuery("");
      setIsDropdownOpen(false);
    }

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    setFiles((prevFiles) => {
      const fileMap = new Map(prevFiles.map((file) => [file.name, file]));

      selectedFiles.forEach((file) => {
        if (!fileMap.has(file.name)) {
          fileMap.set(file.name, file);
        }
      });

      return Array.from(fileMap.values());
    });
  };

  const removeFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  // Medical task handlers
  const fetchMedicalManagerBalagruhas = async () => {
    try {
      const userId = localStorage.getItem("userId");
      const response = await getBalagruhaById(userId);
      if (response.success && response.data) {
        setAvailableBalagruhas(response.data.balagruhas || []);
      }
    } catch (error) {
      console.error("Error fetching balagruhas:", error);
    }
  };

  const fetchStudentsByBalagruha = async (balagruhaId) => {
    try {
      const response = await getAnyUserBasedonRoleandBalagruha("student", balagruhaId);
      if (response.success) {
        setAvailableStudents(response.data.users || []);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const fetchMedicalManagersByBalagruha = async () => {
    try {
      const currentUserId = localStorage.getItem("userId");
      const allUsers = [];

      // All roles except 'student'
      const rolesToFetch = [
        "admin",
        "coach",
        "balagruha-incharge",
        "purchase-manager",
        "medical-incharge",
        "sports-coach",
        "music-coach",
        "amma"
      ];

      // Fetch admins first (they're global, not tied to specific balagruhas)
      const adminResponse = await getAnyUserBasedonRoleandBalagruha("admin", availableBalagruhas[0]?._id);
      if (adminResponse.success) {
        const admins = adminResponse.data.users || [];
        allUsers.push(...admins);
      }

      // Fetch all other roles from ALL balagruhas that the current user has access to
      for (const balagruha of availableBalagruhas) {
        for (const role of rolesToFetch) {
          if (role !== "admin") { // Skip admin since we already fetched them globally
            const response = await getAnyUserBasedonRoleandBalagruha(role, balagruha._id);
            if (response.success) {
              const users = response.data.users || [];
              allUsers.push(...users);
            }
          }
        }
      }

      // Remove duplicates based on user ID
      const uniqueUsers = Array.from(new Map(allUsers.map(u => [u._id, u])).values());
      setAvailableMedicalManagers(uniqueUsers);
    } catch (error) {
      console.error("Error fetching assignable users:", error);
    }
  };

  const handleBalagruhaSelect = async (balagruhaId) => {
    setFormData((prev) => ({
      ...prev,
      medicalBalagruhaId: balagruhaId,
      students: [],
      assignedUser: [],
    }));
    setAvailableStudents([]);
    await fetchStudentsByBalagruha(balagruhaId);
    await fetchMedicalManagersByBalagruha();
    setIsBalagruhaDropdownOpen(false);
  };

  const handleStudentToggle = (studentId) => {
    setFormData((prev) => ({
      ...prev,
      students: prev.students.includes(studentId)
        ? prev.students.filter((id) => id !== studentId)
        : [...prev.students, studentId],
    }));
  };

  const handleAssignToSelect = (userId) => {
    setFormData((prev) => ({ ...prev, assignedUser: [userId] }));
    setIsAssignToDropdownOpen(false);
    setAssignToSearchQuery(""); // Clear search when user is selected
  };

  // Fetch balagruhas when medical type is selected
  useEffect(() => {
    if (type === "medical" && (role === "medical-manager" || role === "medical-incharge")) {
      fetchMedicalManagerBalagruhas();
    }
  }, [type, role]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (balagruhaDropdownRef.current && !balagruhaDropdownRef.current.contains(event.target)) {
        setIsBalagruhaDropdownOpen(false);
      }
      if (studentDropdownRef.current && !studentDropdownRef.current.contains(event.target)) {
        setIsStudentDropdownOpen(false);
      }
      if (assignToDropdownRef.current && !assignToDropdownRef.current.contains(event.target)) {
        setIsAssignToDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = "Title is required";
    } else if (formData.title.trim().length < 3) {
      errors.title = "Title must be at least 3 characters";
    } else if (formData.title.trim().length > 100) {
      errors.title = "Title cannot exceed 100 characters";
    }


    if (!formData.description.trim()) {
      errors.description = "Description is required";
    } else if (formData.description.trim().length < 10) {
      errors.description = "Description must be at least 10 characters";
    } else if (formData.description.trim().length > 1000) {
      errors.description = "Description cannot exceed 1000 characters";
    }

    if (!formData.deadline) {
      errors.deadline = "Deadline is required";
    } else if (new Date(formData.deadline) < new Date().setHours(0, 0, 0, 0)) {
      errors.deadline = "Deadline cannot be in the past";
    }

    if (!formData.assignedUser || formData.assignedUser.length === 0) {
      errors.assignedUser = "At least one user must be assigned";
    }

    if (type === "medical") {
      if (!formData.medicalBalagruhaId) {
        errors.medicalBalagruhaId = "Please select a balagruha";
      }
    }

    if (type !== "medical" && !formData.balagruhaId) {
      errors.balagruhaId = "Please select a balagruha";
    }

    if (
      (type === "purchase" || type === "order") &&
      formData.costEstimate
    ) {
      if (Number(formData.costEstimate) <= 0) {
        errors.costEstimate =
          "Cost estimate must be greater than 0";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setFormErrors((prev) => ({
        ...prev,
        submit: "Please fill all required fields before submitting.",
      }));
      return;
    }

    const taskData = {
      ...formData,
      type: type,
      attachments: files,
    };

    // For medical tasks, use medicalBalagruhaId as balagruhaId
    if (type === "medical") {
      taskData.balagruhaId = formData.medicalBalagruhaId;
      taskData.students = formData.students;
    }

    onSubmit(taskData);
  };

  const getAvailableTaskTypes = () => {
    switch (role) {
      case "sports-coach":
        return taskTypes.filter(
          (type) => type.id === "sports" || type.id === "general"
        );
      case "music-coach":
        return taskTypes.filter(
          (type) => type.id === "music" || type.id === "general"
        );
      case "purchase-manager":
        return taskTypes.filter(
          (type) => type.id === "purchase" || type.id === "general"
        );
      case "medical-manager":
      case "medical-incharge":
        return taskTypes.filter(
          (type) => type.id === "medical" || type.id === "general"
        );
      default:
        return taskTypes;
    }
  };

  return (
    <div className="create-task-container">
      <div className="create-task-header">
        <h2>Create New Task</h2>
        <button className="close-button" onClick={onCancel}>
          ×
        </button>
      </div>

      <form className="create-task-form" onSubmit={handleSubmit}>
        <div className="form-group" ref={typeDropdownRef}>
          <label>Task Type</label>
          <div className="custom-dropdown" style={{ position: "relative" }}>
            <button
              type="button"
              className="dropdown-toggle"
              onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
            >
              {getSelectedTypeLabel()}
              <span className="arrow">&#9662;</span>
            </button>

            {isTypeDropdownOpen && (
              <div className="dropdown-menu">
                {taskTypes.map((taskType) => (
                  <div
                    key={taskType.id}
                    className={`dropdown-item ${type === taskType.id ? "selected" : ""
                      }`}
                    onClick={() => handleTypeSelect(taskType.id)}
                  >
                    {taskType.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="title">
            Task Title <span className="required">*</span>
          </label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className={formErrors.title ? "error" : ""}
            placeholder="Enter task title"
          />
          {formErrors.title && (
            <div className="error-message">{formErrors.title}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="description">
            Description <span className="required">*</span>
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            className={formErrors.description ? "error" : ""}
            placeholder="Enter task description"
            rows="4"
          ></textarea>
          {formErrors.description && (
            <div className="error-message">{formErrors.description}</div>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="priority">Priority</label>
            <select
              id="priority"
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="deadline">
              Deadline <span className="required">*</span>
            </label>
            <input
              type="datetime-local"
              id="deadline"
              name="deadline"
              value={formData.deadline}
              onChange={handleInputChange}
              className={formErrors.deadline ? "error" : ""}
              min={new Date().toISOString().slice(0, 16)}
            />
            {formErrors.deadline && (
              <div className="error-message">{formErrors.deadline}</div>
            )}
          </div>
        </div>

        {type !== "medical" && (
          <div className="form-group">
            <label htmlFor="balagruhaId">
              Balagruha <span className="required">*</span>
            </label>

            <SearchableSelect
              searchPlaceholder="Search balagruha..."
              id="balagruhaId"
              name="balagruhaId"
              value={formData.balagruhaId}
              onChange={handleInputChange}
              className={formErrors.balagruhaId ? "error" : ""}
            >
              <option value="">Select Balagruha</option>
              {balagruhas.map((balagruha) => (
                <option key={balagruha._id} value={balagruha._id}>
                  {balagruha.name}
                </option>
              ))}
            </SearchableSelect>

            {formErrors.balagruhaId && (
              <div className="error-message">{formErrors.balagruhaId}</div>
            )}
          </div>
        )}

        {/* General Assign To field - exclude medical tasks as they have their own assign logic */}
        {type !== "medical" && (
          <div className="form-group dropdown-container" ref={dropdownRef}>
            <label>Assign To <span className="required">*</span></label>
            <div className="dropdown">
              <button
                key={formData.balagruhaId}
                type="button"
                className="dropdown-toggle"
                onClick={toggleDropdown}
              >
                {assignedUserLabel}
                <span className="arrow">&#9662;</span>
              </button>

              {isDropdownOpen && (
                <div className="dropdown-menu" style={{ maxHeight: "260px", overflowY: "auto" }}>
                  <div className="filter-search" style={{ padding: "8px", borderBottom: "1px solid #e0e0e0" }}>
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={assignSearchQuery}
                      onChange={(e) => setAssignSearchQuery(e.target.value)}
                    />
                  </div>

                  <div className="dropdown-group">
                    <div className="dropdown-group-label" style={{ fontWeight: 600, padding: "8px" }}>
                      Staff
                    </div>
                    {filteredStaffUsers.length > 0 ? (
                      filteredStaffUsers.map((user) => (
                        <label key={user._id} className="dropdown-item">
                          <input
                            type="checkbox"
                            value={user._id}
                            checked={formData.assignedUser.includes(user._id)}
                            onChange={handleCheckboxChange}
                          />
                          <span>
                            {user.name} ({user.role})
                          </span>
                        </label>
                      ))
                    ) : (
                      <div className="dropdown-item" style={{ opacity: 0.7 }}>
                        No staff found
                      </div>
                    )}
                  </div>

                  {/* <div className="dropdown-group">
                    <div className="dropdown-group-label" style={{ fontWeight: 600, padding: "8px" }}>
                      Students
                    </div>
                    {filteredStudentUsers.length > 0 ? (
                      filteredStudentUsers.map((user) => (
                        <label key={user._id} className="dropdown-item">
                          <input
                            type="checkbox"
                            value={user._id}
                            checked={formData.assignedUser.includes(user._id)}
                            onChange={handleCheckboxChange}
                          />
                          {user.name}
                        </label>
                      ))
                    ) : (
                      <div className="dropdown-item" style={{ opacity: 0.7 }}>
                        No students found
                      </div>
                    )}
                  </div> */}
                </div>
              )}
            </div>
            {formErrors.assignedUser && (
              <div className="error-message">{formErrors.assignedUser}</div>
            )}
          </div>
        )}

        {(localStorage.getItem("role") === "sports-coach" ||
          type == "sports" ||
          localStorage.getItem("role") === "music-coach" ||
          type == "music") && (
            <div className="form-group">
              <label htmlFor="drillOrExerciseType">Drill or Exercise Type</label>
              <input
                type="text"
                id="drillOrExerciseType"
                name="drillOrExerciseType" // Changed from 'title' to 'drillOrExerciseType'
                value={formData.drillOrExerciseType}
                onChange={handleInputChange}
                className={formErrors.drillOrExerciseType ? "error" : ""}
                placeholder="Enter drill or exercise type"
              />
              {/* {formErrors.drillOrExerciseType &&
                                <div className="error-message">{formErrors.drillOrExerciseType}</div>
                            } */}
            </div>
          )}

        {(type == "order" ||
          localStorage.getItem("role") === "purchase-manager" ||
          type == "purchase") && (
            <>
              <div className="form-group">
                <label htmlFor="drillOrExerciseType">Machine Details</label>
                <input
                  type="text"
                  id="machineDetails"
                  name="machineDetails"
                  value={formData.machineDetails}
                  onChange={handleInputChange}
                  className={formErrors.machineDetails ? "error" : ""}
                  placeholder="Enter Machine Details"
                />
                {/* {formErrors.drillOrExerciseType &&
                                <div className="error-message">{formErrors.drillOrExerciseType}</div>
                            } */}
              </div>
              <div className="form-group">
                <label htmlFor="drillOrExerciseType">Vendor Details</label>
                <input
                  type="text"
                  id="vendorDetails"
                  name="vendorDetails"
                  value={formData.vendorDetails}
                  onChange={handleInputChange}
                  className={formErrors.vendorDetails ? "error" : ""}
                  placeholder="Enter Vendor Details"
                />
                {/* {formErrors.drillOrExerciseType &&
                                <div className="error-message">{formErrors.drillOrExerciseType}</div>
                            } */}
              </div>

              <div className="form-group">
                <label htmlFor="costEstimate">Cost Estimate</label>
                <input
                  type="text"
                  id="costEstimate"
                  name="costEstimate"
                  value={formData.costEstimate}
                  onChange={handleInputChange}
                  className={formErrors.costEstimate ? "error" : ""}
                  placeholder="Enter Cost Estimate"
                />
              </div>
            </>
          )}

        {(localStorage.getItem("role") === "purchase-manager" ||
          type == "purchase") && (
            <div className="form-group">
              <label htmlFor="requiredParts">Required Parts</label>
              <input
                type="text"
                id="requiredParts"
                name="requiredParts"
                value={formData.requiredParts}
                onChange={handleInputChange}
                className={formErrors.requiredParts ? "error" : ""}
                placeholder="Required Parts"
              />
            </div>
          )}
        {(localStorage.getItem("role") === "repair-manager" ||
          type == "order") && (
            <div className="form-group">
              <label htmlFor="repairDetails">Required Parts</label>
              <input
                type="text"
                id="repairDetails"
                name="repairDetails" // Changed from 'title' to 'drillOrExerciseType'
                value={formData.repairDetails}
                onChange={handleInputChange}
                className={formErrors.repairDetails ? "error" : ""}
                placeholder="Required Details"
              />
            </div>
          )}

        {/* Medical Task Specific Fields */}
        {(localStorage.getItem("role") === "medical-manager" || localStorage.getItem("role") === "medical-incharge" || type === "medical") && (
          <>
            {/* Balagruha Selection */}
            <div className="form-group dropdown-container" ref={balagruhaDropdownRef}>
              <label>
                Balagruha <span className="required">*</span>
              </label>
              <div className="dropdown">
                <button
                  type="button"
                  className="dropdown-toggle"
                  onClick={() => setIsBalagruhaDropdownOpen(!isBalagruhaDropdownOpen)}
                >
                  {formData.medicalBalagruhaId
                    ? availableBalagruhas.find((b) => b._id === formData.medicalBalagruhaId)?.name || "Select Balagruha"
                    : "Select Balagruha"}
                  <span className="arrow">&#9662;</span>
                </button>

                {isBalagruhaDropdownOpen && (
                  <div className="dropdown-menu">
                    {availableBalagruhas.map((balagruha) => (
                      <div
                        key={balagruha._id}
                        className={`dropdown-item ${formData.medicalBalagruhaId === balagruha._id ? "selected" : ""
                          }`}
                        onClick={() => handleBalagruhaSelect(balagruha._id)}
                      >
                        {balagruha.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Assign To - Medical Managers or Admin (appears after balagruha selection) */}
            {formData.medicalBalagruhaId && (
              <div className="form-group dropdown-container" ref={assignToDropdownRef}>
                <label>
                  Assign To <span className="required">*</span>
                </label>
                <div className="dropdown">
                  <button
                    type="button"
                    className="dropdown-toggle"
                    onClick={() => setIsAssignToDropdownOpen(!isAssignToDropdownOpen)}
                  >
                    {primaryAssigneeId
                      ? availableMedicalManagers.find((u) => u._id === primaryAssigneeId)?.name || "Select User"
                      : "Select User"}
                    <span className="arrow">&#9662;</span>
                  </button>

                  {isAssignToDropdownOpen && (
                    <div className="dropdown-menu">
                      {/* Search input */}
                      <div style={{ padding: "8px", borderBottom: "1px solid #e0e0e0" }}>
                        <input
                          type="text"
                          placeholder="Search users..."
                          value={assignToSearchQuery}
                          onChange={(e) => setAssignToSearchQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: "100%",
                            padding: "6px 10px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px"
                          }}
                        />
                      </div>
                      {/* Filtered users list */}
                      {(() => {
                        const filteredUsers = availableMedicalManagers.filter((user) =>
                          user.name.toLowerCase().includes(assignToSearchQuery.toLowerCase()) ||
                          user.role.toLowerCase().includes(assignToSearchQuery.toLowerCase())
                        );
                        return filteredUsers.length > 0 ? (
                          filteredUsers.map((user) => (
                            <div
                              key={user._id}
                              className={`dropdown-item ${primaryAssigneeId === user._id ? "selected" : ""
                                }`}
                              onClick={() => handleAssignToSelect(user._id)}
                            >
                              {user.name} ({user.role})
                            </div>
                          ))
                        ) : (
                          <div className="dropdown-item" style={{ opacity: 0.6 }}>
                            No users found
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Students Multi-Select (appears after balagruha selection) */}
            {formData.medicalBalagruhaId && (
              <div className="form-group dropdown-container" ref={studentDropdownRef}>
                <label>Select Students (Optional)</label>
                <div className="dropdown">
                  <button
                    type="button"
                    className="dropdown-toggle"
                    onClick={() => setIsStudentDropdownOpen(!isStudentDropdownOpen)}
                  >
                    {formData.students.length > 0
                      ? `${formData.students.length} student${formData.students.length > 1 ? "s" : ""} selected`
                      : "Select Students"}
                    <span className="arrow">&#9662;</span>
                  </button>

                  {isStudentDropdownOpen && (
                    <div className="dropdown-menu" style={{ maxHeight: "250px", overflowY: "auto" }}>
                      {availableStudents.length > 0 ? (
                        availableStudents.map((student) => (
                          <label key={student._id} className="dropdown-item">
                            <input
                              type="checkbox"
                              checked={formData.students.includes(student._id)}
                              onChange={() => handleStudentToggle(student._id)}
                            />
                            {student.name}
                          </label>
                        ))
                      ) : (
                        <div className="dropdown-item" style={{ opacity: 0.6 }}>
                          No students found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <div className="form-group">
          <label>Attachments</label>
          <div className="file-upload-container">
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              ref={fileInputRef}
            // style={{ display: 'none' }}
            />
            <button
              type="button"
              className="upload-button"
              onClick={() => fileInputRef.current.click()}
            >
              Upload Files
            </button>
          </div>

          {files.length > 0 && (
            <div className="file-preview-list">
              {files.map((file, index) => (
                <div key={index} className="file-preview-container">
                  <FilePreview file={file} />
                  <button
                    type="button"
                    className="remove-file-button"
                    onClick={() => removeFile(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {formErrors.submit && (
          <div
            className="error-message"
            style={{
              marginBottom: "12px",
              textAlign: "center",
              color: "red",
              fontWeight: "500",
            }}
          >
            {formErrors.submit}
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="submit-button">
            Create Task
          </button>
        </div>
      </form>
    </div>
  );
};

// Task Details Modal Component
// export const TaskDetailsModal = ({ task, getTask, onClose, users, onStatusChange, onUpdateTask }) => {
//     const [activeTab, setActiveTab] = useState('details');
//     const [commentText, setCommentText] = useState('');
//     const [files, setFiles] = useState([]);
//     const [comments, setComments] = useState(task.comments || []);
//     const [isUpdating, setIsUpdating] = useState(false);
//     const [showDeleteModal, setShowDeleteModal] = useState(false)
//     const fileInputRef = useRef(null);
//     const [selectedAttachmentId, setSelectedAttachmentId] = useState('')
//     const [selectedTask, setSelectedTask] = useState(task)
//     const currentUser = { _id: localStorage.getItem('userId') };
//     
//     if (!task) return null;

//     const assignedUser = users.find(user => user._id === selectedTask.assignedUser);
//     const createdByUser = users.find(user => user._id === selectedTask.createdBy);

//     const formatDate = (dateString) => {
//         const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
//         return new Date(dateString).toLocaleDateString(undefined, options);
//     };

//     const handleStatusChange = async (newStatus) => {
//         if (isUpdating) return;

//         setIsUpdating(true);
//         try {
//             const data = { status: newStatus };
//             const response = await updateTask(selectedTask._id, JSON.stringify(data));

//             if (response && response.success) {

//                 if (onStatusChange) {
//                     await onStatusChange(selectedTask._id, newStatus);
//                 }
//                 getTaskDetailsByTaskId(selectedTask._id)
//             } else {
//                 console.error('Error updating task status:', response?.message || 'Unknown error');
//             }
//         } catch (error) {
//             console.error('Error updating task status:', error);
//         } finally {
//             setIsUpdating(false);
//         }
//     };

//     const handleFileChange = (e) => {
//         const selectedFiles = Array.from(e.target.files);
//         setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
//     };

//     const removeFile = (index) => {
//         setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
//     };

//     const handleAddComment = async (data) => {
//         if (!commentText.trim() && files.length === 0) return;

//         setIsUpdating(true);
//         try {
//             const formData = new FormData();
//             formData.append('comments', commentText)
//             if (files.length > 0) {
//                 files.forEach(file => {
//                     formData.append('attachments', file);
//                 });
//             }

//             setIsUpdating(true);
//             let response;
//             if (localStorage.getItem('role') === "sports-coach") {
//                 response = await addOrUpdateSportsTaskComments(selectedTask._id, formData)
//             } else if (localStorage.getItem('role') === "music-coach") {
//                 response = await addOrUpdateMusicTaskComments(selectedTask._id, formData)
//             }
//             else {
//                 response = await addComment(selectedTask._id, formData);
//             }

//             getTaskDetailsByTaskId(selectedTask?._id)
//             

//             if (response && response.success) {
//                 const updatedTask = response.data.task;
//                 setComments(updatedTask.comments || []);
//                 if (onUpdateTask) {
//                     await onUpdateTask(selectedTask._id, { comments: updatedTask.comments });
//                 }
//                 setCommentText('');
//                 setFiles([]);
//                 setIsUpdating(false);

//             }
//         } catch (error) {
//             console.error('Error adding comment:', error);
//             alert('Failed to add comment');
//             setIsUpdating(false);
//         } finally {
//             setIsUpdating(false);
//         }
//     };

//     const getTaskDetailsByTaskId = async (id) => {
//         try {
//             const response = await getTaskBytaskId(id)
//             setSelectedTask(response.data?.task)
//         } catch (err) {
//             console.error('Error updating task status:', err);

//         }
//     }

//     const handleUploadFiles = async () => {
//         if (files.length === 0) return;

//         setIsUpdating(true);
//         try {
//             const formData = new FormData();
//             if (files.length > 0) {
//                 files.forEach(file => {
//                     formData.append('attachments', file);
//                 });
//             }
//             if (localStorage.getItem('role') === "sports-coach") {
//                 await addOrUpdateSportsTaskAttachments(selectedTask._id, formData)
//             } else if (localStorage.getItem('role') === "music-coach") {
//                 await addOrUpdateMusicTaskAttachments(selectedTask._id, formData)
//             } else {
//                 await updateTaskAttachments(selectedTask._id, formData);
//             }
//             getTaskDetailsByTaskId(selectedTask._id)
//             setFiles([])
//             // getAllTasks()
//         }

//         catch (error) {
//             console.error('Error uploading files:', error);
//         } finally {
//             setIsUpdating(false);
//         }
//     };

//     const deleteAttachments = async () => {
//         
//         const response = await deleteAttachemnets(task._id, selectedAttachmentId);
//         getTaskDetailsByTaskId(selectedTask._id)
//         setShowDeleteModal(false)
//     }

//     const handleDeleteComment = async (commentId) => {
//         const response = await deleteCommentinTask(selectedTask?._id, commentId)
//         
//         setComments((prevComments) => response?.data?.task?.comments || prevComments);
//     }

//     const priorityColors = {
//         High: '#ff7979',
//         Medium: '#f6e58d',
//         Low: '#badc58'
//     };

//     const isOverdue = new Date(selectedTask.deadline) < new Date() && selectedTask.status.toLowerCase() !== 'completed';

//     return (
//         <div className="modal-overlay" onClick={onClose}>
//             <div className="task-details-modal" onClick={e => e.stopPropagation()}>
//                 <div className="modal-header" style={{ borderBottom: `4px solid ${priorityColors[task.priority]}` }}>
//                     <h2>{task.title}</h2>
//                     <button className="close-button" onClick={onClose}>×</button>
//                 </div>

//                 <div className="modal-tabs">
//                     <button
//                         className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
//                         onClick={() => setActiveTab('details')}
//                     >
//                         📋 Details
//                     </button>
//                     <button
//                         className={`tab-button ${activeTab === 'files' ? 'active' : ''}`}
//                         onClick={() => setActiveTab('files')}
//                     >
//                         📁 Files
//                     </button>
//                     <button
//                         className={`tab-button ${activeTab === 'comments' ? 'active' : ''}`}
//                         onClick={() => setActiveTab('comments')}
//                     >
//                         💬 Comments
//                     </button>
//                 </div>

//                 <div className="modal-body">
//                     {activeTab === 'details' && (
//                         <div className="task-info">
//                             <div className="info-section">
//                                 <h3>Description</h3>
//                                 <p>{selectedTask.description}</p>
//                             </div>

//                             <div className="info-grid">
//                                 <div className="info-item">
//                                     <span className="info-label">Status</span>
//                                     <span className={`status-badge status-${selectedTask.status.replace(/\s+/g, '-').toLowerCase()}`}>
//                                         {selectedTask.status}
//                                     </span>
//                                 </div>

//                                 <div className="info-item">
//                                     <span className="info-label">Priority</span>
//                                     <span className={`priority-badge priority-${selectedTask.priority.toLowerCase()}`}>
//                                         {selectedTask.priority.toLowerCase() === 'high' ? '🔴 High' :
//                                             selectedTask.priority.toLowerCase() === 'medium' ? '🟡 Medium' : '🟢 Low'}
//                                     </span>
//                                 </div>

//                                 <div className="info-item">
//                                     <span className="info-label">Deadline</span>
//                                     <span className={`deadline-text ${isOverdue ? 'overdue' : ''}`}>
//                                         {isOverdue && '⚠️ '}{formatDate(selectedTask.deadline)}
//                                     </span>
//                                 </div>

//                                 <div className="info-item">
//                                     <span className="info-label">Created</span>
//                                     <span>{formatDate(selectedTask.createdAt)}</span>
//                                 </div>

//                                 <div className="info-item">
//                                     <span className="info-label">Assigned To</span>

//                                     <span>{selectedTask?.assignedUser ? selectedTask?.assignedUser.name : 'Unassigned'}</span>
//                                 </div>

//                                 <div className="info-item">
//                                     <span className="info-label">Created By</span>
//                                     <span>{selectedTask?.createdBy ? selectedTask?.createdBy.name : 'Unknown'}</span>
//                                 </div>
//                                 <div className="info-item">
//                                     <span className="info-label">Type</span>
//                                     <span>{selectedTask?.type ? selectedTask?.type : '--'}</span>
//                                 </div>
//                             </div>
//                         </div>
//                     )}

//                     {activeTab === 'files' && (
//                         <div className="files-tab">
//                             <div className="files-header">
//                                 <h3>Task Attachments</h3>
//                                 <div className="file-upload-container">
//                                     <input
//                                         type="file"
//                                         multiple
//                                         onChange={handleFileChange}
//                                         ref={fileInputRef}
//                                         style={{ display: 'none' }}
//                                     />
//                                     <button
//                                         className="upload-button"
//                                         onClick={() => fileInputRef.current.click()}
//                                     >
//                                         Upload Files
//                                     </button>
//                                 </div>
//                             </div>

//                             {selectedTask.attachments && selectedTask.attachments.length > 0 ? (
//                                 <div className="existing-files">
//                                     <h4>Existing Files</h4>
//                                     <div className="file-list">
//                                         {selectedTask.attachments.map((file, index) => (
//                                             <div key={index} className="file-item">
//                                                 <div className="file-icon">
//                                                     {file.fileType?.startsWith('image/') ? '🖼️' :
//                                                         file.fileType === 'application/pdf' ? '📄' : '📎'}
//                                                 </div>
//                                                 <div className="file-details">
//                                                     <div className="file-name">{file.name}</div>
//                                                     <div className="file-meta">
//                                                         <span className="file-size">
//                                                             {file.fileName}
//                                                         </span>
//                                                         <div className='download-wrapper'>
//                                                             <a
//                                                                 href={file.fileUrl}
//                                                                 target="_blank"
//                                                                 rel="noopener noreferrer"
//                                                                 className="file-download"
//                                                             >
//                                                                 Download
//                                                             </a>

//                                                             <div

//                                                                 className="file-download file-download-delete"
//                                                                 onClick={() => { setSelectedAttachmentId(file._id); setShowDeleteModal(true) }}
//                                                             >
//                                                                 Delete
//                                                             </div>
//                                                         </div>
//                                                     </div>
//                                                 </div>
//                                             </div>
//                                         ))}
//                                     </div>
//                                 </div>
//                             ) : (
//                                 <div className="no-files-message">
//                                     <div className="no-files-icon">📂</div>
//                                     <p>No files attached to this task yet</p>
//                                 </div>
//                             )}

//                             {files.length > 0 && (
//                                 <div className="new-files">
//                                     <h4>New Files to Upload</h4>
//                                     <div className="file-preview-list">
//                                         {files.map((file, index) => (
//                                             <div key={index} className="file-preview-container">
//                                                 <FilePreview file={file} />
//                                                 <button
//                                                     className="remove-file-button"
//                                                     onClick={() => removeFile(index)}
//                                                 >
//                                                     ×
//                                                 </button>
//                                             </div>
//                                         ))}
//                                     </div>
//                                     <button
//                                         className="upload-all-button"
//                                         onClick={handleUploadFiles}
//                                         disabled={isUpdating}
//                                     >
//                                         {isUpdating ? 'Uploading...' : 'Upload All Files'}
//                                     </button>
//                                 </div>
//                             )}
//                         </div>
//                     )}

//                     {showDeleteModal && (
//                         <div className="modal-overlay">
//                             <div className="modal-content">
//                                 <div className="modal-header">
//                                     <div className="modal-icon">⚠️</div>
//                                     <h3 className="modal-title">Confirm Deletion</h3>
//                                 </div>
//                                 <div className="modal-body">
//                                     <p>Are you sure you want to delete the attachment <strong>{selectedTask.fileName}</strong>?</p>
//                                     <p>This action cannot be undone.</p>
//                                 </div>
//                                 <div className="modal-actions">
//                                     <button
//                                         className="delete-confirm-button"
//                                         onClick={() => deleteAttachments()}
//                                     >
//                                         Yes, Delete Attachments
//                                     </button>
//                                     <button
//                                         className="cancel-button"
//                                         onClick={() => setShowDeleteModal(false)}
//                                     >
//                                         Cancel
//                                     </button>
//                                 </div>
//                             </div>
//                         </div>
//                     )}

//                     {activeTab === 'comments' && (
//                         <div className="comments-tab">
//                             <h3>Comments</h3>

//                             <div className="comments-list">
//                                 {comments.length > 0 ? (
//                                     comments.map((comment, index) => (
//                                         <Comment key={index} comment={comment} users={users} onDelete={handleDeleteComment} />
//                                     ))
//                                 ) : (
//                                     <div className="no-comments-message">
//                                         <div className="no-comments-icon">💬</div>
//                                         <p>No comments yet. Be the first to comment!</p>
//                                     </div>
//                                 )}
//                             </div>

//                             <div className="add-comment">
//                                 <textarea
//                                     className="comment-input"
//                                     placeholder="Add a comment..."
//                                     value={commentText}
//                                     onChange={(e) => setCommentText(e.target.value)}
//                                 ></textarea>

//                                 <div className="comment-actions">
//                                     <div className="file-upload-container">
//                                         <input
//                                             type="file"
//                                             multiple
//                                             onChange={handleFileChange}
//                                             ref={fileInputRef}
//                                             style={{ display: 'none' }}
//                                         />
//                                         <button
//                                             className="attach-button"
//                                             onClick={() => fileInputRef.current.click()}
//                                         >
//                                             📎 Attach Files
//                                         </button>
//                                     </div>

//                                     <button
//                                         className="post-comment-button"
//                                         onClick={(task) => handleAddComment(task)}
//                                         disabled={(!commentText.trim() && files.length === 0) || isUpdating}
//                                     >
//                                         {isUpdating ? 'Posting...' : 'Post Comment'}
//                                     </button>
//                                 </div>

//                                 {files.length > 0 && (
//                                     <div className="comment-attachments-preview">
//                                         {files.map((file, index) => (
//                                             <div key={index} className="attachment-preview">
//                                                 <span className="attachment-name">{file.name}</span>
//                                                 <button
//                                                     className="remove-attachment"
//                                                     onClick={() => removeFile(index)}
//                                                 >
//                                                     ×
//                                                 </button>
//                                             </div>
//                                         ))}
//                                     </div>
//                                 )}
//                             </div>
//                         </div>
//                     )}
//                 </div>

//                 <div className="modal-footer">
//                     <div className="status-actions">
//                         <button
//                             className={`status-button pending ${selectedTask.status.toLowerCase() === 'pending' ? 'active' : ''}`}
//                             onClick={() => handleStatusChange('pending')}
//                             disabled={selectedTask.status.toLowerCase() === 'pending' || isUpdating}
//                         >
//                             {isUpdating ? 'Updating...' : 'Waiting to Start'}
//                         </button>
//                         <button
//                             className={`status-button in-progress ${selectedTask.status.toLowerCase() === 'in progress' ? 'active' : ''}`}
//                             onClick={() => handleStatusChange('in progress')}
//                             disabled={selectedTask.status.toLowerCase() === 'in progress' || isUpdating}
//                         >
//                             {isUpdating ? 'Updating...' : 'Working On It'}
//                         </button>
//                         <button
//                             className={`status-button completed ${selectedTask.status.toLowerCase() === 'completed' ? 'active' : ''}`}
//                             onClick={() => handleStatusChange('completed')}
//                             disabled={selectedTask.status.toLowerCase() === 'completed' || isUpdating}
//                         >
//                             {isUpdating ? 'Updating...' : 'All Done!'}
//                         </button>
//                     </div>
//                 </div>
//             </div>
//         </div>
//     );
// };

// ---------------------------------------------------------------------------
// Task attachment card with a preview. Files live in a private bucket, so they are fetched through
// the API (GET /api/tasks/attachments/:taskId/:attachmentId/file) and shown from a blob URL.
// ---------------------------------------------------------------------------
const attachmentName = (file) => {
  const raw = typeof file === "string" ? file : file.fileName || file.name || "Attachment";
  return String(raw).split("\\").pop().split("/").pop();
};
const attachmentKind = (name, type = "") => {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (type.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) return "image";
  if (type === "application/pdf" || ext === "pdf") return "pdf";
  if (["doc", "docx"].includes(ext)) return "doc";
  if (["xls", "xlsx", "csv"].includes(ext)) return "sheet";
  if (["ppt", "pptx"].includes(ext)) return "slides";
  if (type.startsWith("video/") || ["mp4", "webm", "mov"].includes(ext)) return "video";
  if (type.startsWith("audio/") || ["mp3", "wav", "ogg", "m4a"].includes(ext)) return "audio";
  return "file";
};
const KIND_LABEL = { pdf: "PDF", doc: "Word", sheet: "Spreadsheet", slides: "Slides", video: "Video", audio: "Audio", file: "File" };
const KIND_ICON = { pdf: "📕", doc: "📘", sheet: "📗", slides: "📙", video: "🎬", audio: "🎵", file: "📄" };

const AttachmentCard = ({ taskId, file }) => {
  const name = attachmentName(file);
  const kind = attachmentKind(name, file?.fileType || "");
  const attachmentId = typeof file === "object" ? file._id : null;
  const [blobUrl, setBlobUrl] = useState("");
  const [status, setStatus] = useState(kind === "image" && attachmentId ? "loading" : "idle");
  const blobUrlRef = useRef("");

  const loadBlob = async () => {
    if (blobUrlRef.current) return blobUrlRef.current;
    const blob = await getTaskAttachmentBlob(taskId, attachmentId);
    const url = URL.createObjectURL(blob);
    blobUrlRef.current = url;
    setBlobUrl(url);
    return url;
  };

  useEffect(() => {
    let cancelled = false;
    if (kind === "image" && attachmentId) {
      loadBlob()
        .then(() => { if (!cancelled) setStatus("ready"); })
        .catch(() => { if (!cancelled) setStatus("missing"); });
    }
    return () => {
      cancelled = true;
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = "";
    };
  }, [taskId, attachmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (download) => {
    if (!attachmentId) return;
    // Opened synchronously so the browser doesn't treat it as a pop-up.
    const tab = download ? null : window.open("", "_blank");
    try {
      const url = await loadBlob();
      if (download) {
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        a.click();
      } else if (tab) {
        tab.location.href = url;
      }
      setStatus((st) => (st === "loading" ? st : "ready"));
    } catch {
      if (tab) tab.close();
      setStatus("missing");
    }
  };

  return (
    <div className={`tm-att${status === "missing" ? " is-missing" : ""}`}>
      <button
        type="button"
        className="tm-att__preview"
        onClick={() => open(false)}
        disabled={!attachmentId || status === "missing"}
        title={status === "missing" ? "This file is no longer available" : `Open ${name}`}
      >
        {kind === "image" && status === "ready" && blobUrl ? (
          <img src={blobUrl} alt={name} />
        ) : status === "loading" ? (
          <span className="tm-att__placeholder">Loading preview…</span>
        ) : (
          <span className="tm-att__icon" aria-hidden="true">{status === "missing" ? "⚠️" : KIND_ICON[kind] || "🖼️"}</span>
        )}
      </button>
      <div className="tm-att__meta">
        <span className="tm-att__name" title={name}>{name}</span>
        <span className="tm-att__kind">
          {status === "missing" ? "File not available" : kind === "image" ? "Image" : KIND_LABEL[kind]}
        </span>
      </div>
      <div className="tm-att__actions">
        <button type="button" onClick={() => open(false)} disabled={!attachmentId || status === "missing"}>View</button>
        <button type="button" onClick={() => open(true)} disabled={!attachmentId || status === "missing"}>Download</button>
      </div>
    </div>
  );
};

export const TaskDetailsModal = ({
  task,
  getTask,
  onClose,
  users,
  onStatusChange,
  onUpdateTask,
  initialTab = "details",
}) => {
  // Comments now live in the task chat panel (TaskChatPanel); old links to that tab show Details.
  const [activeTab, setActiveTab] = useState(initialTab === "comments" ? "details" : initialTab);
  const [files, setFiles] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState("");
  const [selectedTask, setSelectedTask] = useState(task);
  const [editMode, setEditMode] = useState({});
  const [editedValues, setEditedValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const currentUser = { _id: localStorage.getItem("userId") };
  const userRole = (localStorage.getItem("role") || "").toLowerCase();
  const canManageTask = userRole !== "student";

  useEffect(() => {
    if (!task) return;
    setSelectedTask(task);
    setEditMode({});
    setEditedValues({});

    const fetchFullTask = async () => {
      if (task._id) {
        const refreshed = await getTaskDetailsByTaskId(task._id);
        if (refreshed) {
          setSelectedTask(refreshed);
        }
      }
    };

    fetchFullTask();
  }, [task]);

  if (!task) return null;

  const assignedUser = users?.find(
    (user) => user._id === selectedTask.assignedUser
  );
  const createdByUser = users?.find(
    (user) => user._id === selectedTask.createdBy
  );

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  const currentStatus = (selectedTask.status || "").toLowerCase();
  const openSubtasks = (selectedTask.subtasks || []).filter(
    (st) => (st.status || "").toLowerCase() !== "completed"
  ).length;

  const handleStatusChange = async (newStatus) => {
    if (!canManageTask || isUpdating || newStatus === currentStatus) return;
    // The server refuses "completed" while sub-tasks are open; say so instead of failing silently.
    if (newStatus === "completed" && openSubtasks > 0) {
      resultDialog("info", "Finish the sub-tasks first", `This task still has ${openSubtasks} open sub-task${openSubtasks > 1 ? "s" : ""}. Complete ${openSubtasks > 1 ? "them" : "it"} before marking the task All Done.`);
      return;
    }

    setIsUpdating(true);
    try {
      if (onStatusChange) {
        await onStatusChange(selectedTask._id, newStatus);
      } else {
        await updateTask(selectedTask._id, JSON.stringify({ status: newStatus }));
      }
      await getTaskDetailsByTaskId(selectedTask._id);
    } catch (error) {
      resultDialog("error", "Could not update the status", error?.response?.data?.message || "Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const resolveTaskResponse = (response) => {
    if (!response) return null;
    if (response.task) return response.task;
    if (response.data) {
      if (response.data.task) return response.data.task;
      if (response.data.data) {
        if (response.data.data.task) return response.data.data.task;
        return response.data.data;
      }
      return response.data;
    }
    return response;
  };

  const getTaskDetailsByTaskId = async (id) => {
    try {
      const response = await getTaskBytaskId(id);
      const task = resolveTaskResponse(response);
      if (task) {
        setSelectedTask(task);
      }
      return task;
    } catch (err) {
      console.error("Error fetching task details:", err);
    }
  };

  const handleUploadFiles = async () => {
    if (!canManageTask || files.length === 0) return;

    setIsUpdating(true);
    try {
      const formData = new FormData();
      if (files.length > 0) {
        files.forEach((file) => {
          formData.append("attachments", file);
        });
      }
      let uploadResponse = null;
      if (localStorage.getItem("role") === "sports-coach") {
        uploadResponse = await addOrUpdateSportsTaskAttachments(
          selectedTask._id,
          formData
        );
      } else if (localStorage.getItem("role") === "music-coach") {
        uploadResponse = await addOrUpdateMusicTaskAttachments(
          selectedTask._id,
          formData
        );
      } else {
        uploadResponse = await updateTaskAttachments(selectedTask._id, formData);
      }
      console.debug("Upload response for attachments:", uploadResponse);
      // Refresh task and update UI
      await getTaskDetailsByTaskId(selectedTask._id);
      setFiles([]);
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const deleteAttachments = async () => {
    if (!canManageTask) {
      return;
    }

    const response = await deleteAttachemnets(task._id, selectedAttachmentId);
    getTaskDetailsByTaskId(selectedTask._id);
    setShowDeleteModal(false);
  };

  // Enable edit mode for a specific field
  const enableEditMode = (field) => {
    if (!canManageTask) return;

    setEditMode((prev) => ({ ...prev, [field]: true }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));

    setEditedValues((prev) => ({
      ...prev,
      [field]:
        field === "assignedUser"
          ? selectedTask.assignedUser?._id
          : field === "deadline"
            ? formatDateForInput(selectedTask.deadline)
            : selectedTask[field],
    }));
  };

  // Handle changes to editable fields
  const handleEditChange = (field, value) => {
    setEditedValues((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };
  const saveFieldChange = async (field) => {
    if (!canManageTask) return;

    if (field === "deadline") {
      const deadline = editedValues.deadline;
      const parsedDeadline = deadline ? new Date(deadline) : null;

      if (!deadline || Number.isNaN(parsedDeadline?.getTime())) {
        setFieldErrors((prev) => ({
          ...prev,
          deadline: "Enter a valid deadline date and time.",
        }));
        return;
      }

      if (parsedDeadline <= new Date()) {
        setFieldErrors((prev) => ({
          ...prev,
          deadline: "Deadline must be in the future.",
        }));
        return;
      }
    }

    setIsUpdating(true);

    try {
      let data = {};

      if (field === "assignedUser") {
        data = { assignedUser: editedValues[field] };
      } else {
        data = {
          [field]: field === "deadline"
            ? new Date(editedValues.deadline).toISOString()
            : editedValues[field],
        };
      }

      const response = await updateTask(selectedTask._id, JSON.stringify(data));
      const updatedTask =
        response?.task || response?.data?.task || response?.data || response;

      console.log("Update response:", response);
      console.log("Updated task object:", updatedTask);

      if (updatedTask && (updatedTask._id || updatedTask.id)) {
        const refreshedTask = await getTaskBytaskId(selectedTask._id);

        console.log("Refreshed task:", refreshedTask);

        if (refreshedTask) {
          setSelectedTask(refreshedTask);
        }

        setEditMode((prev) => ({ ...prev, [field]: false }));
      } else {
        console.error(
          `Error updating ${field}:`,
          response?.message || response?.data?.message || "Unknown error"
        );
      }
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
    } finally {
      setIsUpdating(false);
    }
  };
  // Cancel editing for a specific field
  const cancelEdit = (field) => {
    setEditMode((prev) => ({ ...prev, [field]: false }));
    setFieldErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const priorityColors = {
    High: "#ff7979",
    Medium: "#f6e58d",
    Low: "#badc58",
  };

  const isOverdue =
    new Date(selectedTask.deadline) < new Date() &&
    selectedTask.status.toLowerCase() !== "completed";

  console.log("Selected Task:", selectedTask);
  console.log("Attachments:", selectedTask?.attachments);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="task-details-modal" onClick={(e) => e.stopPropagation()}>
        <div
          className="modal-header"
          style={{ borderBottom: `4px solid ${priorityColors[task.priority]}` }}
        >
          {editMode.title ? (
            <div className="edit-field-container">
              <input
                type="text"
                className="edit-field-input"
                value={editedValues.title || ""}
                onChange={(e) => handleEditChange("title", e.target.value)}
              />
              <div className="edit-field-actions">
                <button
                  className="save-edit-button"
                  onClick={() => saveFieldChange("title")}
                  disabled={isUpdating}
                >
                  {isUpdating ? "..." : "✓"}
                </button>
                <button
                  className="cancel-edit-button"
                  onClick={() => cancelEdit("title")}
                >
                  ✕
                </button>
              </div>
            </div>
          ) : (
            <h2
              onClick={canManageTask ? () => enableEditMode("title") : undefined}
              style={{ cursor: canManageTask ? "pointer" : "default" }}
            >
              {selectedTask.title}
              {canManageTask && " ✏️"}
            </h2>
          )}
          <button className="close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-button ${activeTab === "details" ? "active" : ""}`}
            onClick={() => setActiveTab("details")}
          >
            📋 Details
          </button>
          <button
            className={`tab-button ${activeTab === "files" ? "active" : ""}`}
            onClick={() => setActiveTab("files")}
          >
            📁 Files
          </button>
        </div>

        <div className="modal-body">
          {activeTab === "details" && (
            <div className="task-info">
              <div className="info-section">
                <h3>Description</h3>
                {editMode.description ? (
                  <div className="edit-field-container">
                    <textarea
                      className="edit-field-textarea"
                      value={editedValues.description || ""}
                      onChange={(e) =>
                        handleEditChange("description", e.target.value)
                      }
                      rows={4}
                    />
                    <div className="edit-field-actions">
                      <button
                        className="save-edit-button"
                        onClick={() => saveFieldChange("description")}
                        disabled={isUpdating}
                      >
                        {isUpdating ? "..." : "✓"}
                      </button>
                      <button
                        className="cancel-edit-button"
                        onClick={() => cancelEdit("description")}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ) : (
                  <p
                    onClick={
                      canManageTask ? () => enableEditMode("description") : undefined
                    }
                    style={{ cursor: canManageTask ? "pointer" : "default" }}
                  >
                    {selectedTask.description}
                    {canManageTask && " ✏️"}
                  </p>
                )}
              </div>

              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Status</span>
                  {editMode.status ? (
                    <div className="edit-field-container">
                      <select
                        className="edit-field-select"
                        value={editedValues.status || ""}
                        onChange={(e) =>
                          handleEditChange("status", e.target.value)
                        }
                      >
                        <option value="pending">Pending</option>
                        <option value="in progress">In Progress</option>
                        <option value="completed">Completed</option>
                      </select>
                      <div className="edit-field-actions">
                        <button
                          className="save-edit-button"
                          onClick={() => saveFieldChange("status")}
                          disabled={isUpdating}
                        >
                          {isUpdating ? "..." : "✓"}
                        </button>
                        <button
                          className="cancel-edit-button"
                          onClick={() => cancelEdit("status")}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    <span
                      className={`status-badge status-${selectedTask.status
                        .replace(/\s+/g, "-")
                        .toLowerCase()}`}
                      onClick={
                        canManageTask ? () => enableEditMode("status") : undefined
                      }
                      style={{ cursor: canManageTask ? "pointer" : "default" }}
                    >
                      {selectedTask.status}
                      {canManageTask && " ✏️"}
                    </span>
                  )}
                </div>

                <div className="info-item">
                  <span className="info-label">Priority</span>
                  {editMode.priority ? (
                    <div className="edit-field-container">
                      <select
                        className="edit-field-select"
                        value={editedValues.priority || ""}
                        onChange={(e) =>
                          handleEditChange("priority", e.target.value)
                        }
                      >
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                      <div className="edit-field-actions">
                        <button
                          className="save-edit-button"
                          onClick={() => saveFieldChange("priority")}
                          disabled={isUpdating}
                        >
                          {isUpdating ? "..." : "✓"}
                        </button>
                        <button
                          className="cancel-edit-button"
                          onClick={() => cancelEdit("priority")}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    <span
                      className={`priority-badge priority-${selectedTask.priority.toLowerCase()}`}
                      onClick={
                        canManageTask ? () => enableEditMode("priority") : undefined
                      }
                      style={{ cursor: canManageTask ? "pointer" : "default" }}
                    >
                      {selectedTask.priority.toLowerCase() === "high"
                        ? "🔴 High"
                        : selectedTask.priority.toLowerCase() === "medium"
                          ? "🟡 Medium"
                          : "🟢 Low"}
                      {canManageTask && " ✏️"}
                    </span>
                  )}
                </div>

                <div className="info-item">
                  <span className="info-label">Deadline</span>
                  {editMode.deadline ? (
                    <div className="edit-field-container">
                      <input
                        type="datetime-local"
                        id="deadline"
                        name="deadline"
                        className="edit-field-select"
                        value={editedValues.deadline || ""}
                        onChange={(e) =>
                          handleEditChange("deadline", e.target.value)
                        }
                        min={formatDateForInput(new Date())}
                      />
                      {fieldErrors.deadline && (
                        <div className="error-message">{fieldErrors.deadline}</div>
                      )}
                      <div className="edit-field-actions">
                        <button
                          className="save-edit-button"
                          onClick={() => saveFieldChange("deadline")}
                          disabled={isUpdating}
                        >
                          {isUpdating ? "..." : "✓"}
                        </button>
                        <button
                          className="cancel-edit-button"
                          onClick={() => cancelEdit("deadline")}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    <span
                      className={`deadline-text ${isOverdue ? "overdue" : ""}`}
                      onClick={
                        canManageTask ? () => enableEditMode("deadline") : undefined
                      }
                      style={{ cursor: canManageTask ? "pointer" : "default" }}
                    >
                      {isOverdue && "⚠️ "}
                      {formatDate(selectedTask.deadline)}
                      {canManageTask && " ✏️"}
                    </span>
                  )}
                </div>

                <div className="info-item">
                  <span className="info-label">Created</span>
                  <span>{formatDate(selectedTask.createdAt)}</span>
                </div>

                <div className="info-item">
                  <span className="info-label">Assigned To</span>
                  {editMode.assignedUser ? (
                    <div className="edit-field-container">
                      <SearchableSelect
                        searchPlaceholder="Search name..."
                        className="edit-field-select"
                        value={editedValues.assignedUser || ""}
                        onChange={(e) =>
                          handleEditChange("assignedUser", e.target.value)
                        }
                      >
                        <option value="">Unassigned</option>
                        {users?.map((user) => (
                          <option key={user._id} value={user._id}>
                            {user.name}
                          </option>
                        ))}
                      </SearchableSelect>
                      <div className="edit-field-actions">
                        <button
                          className="save-edit-button"
                          onClick={() => saveFieldChange("assignedUser")}
                          disabled={isUpdating}
                        >
                          {isUpdating ? "..." : "✓"}
                        </button>
                        <button
                          className="cancel-edit-button"
                          onClick={() => cancelEdit("assignedUser")}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    <span
                      onClick={
                        canManageTask ? () => enableEditMode("assignedUser") : undefined
                      }
                      style={{ cursor: canManageTask ? "pointer" : "default" }}
                    >
                      {selectedTask?.assignedUser
                        ? selectedTask?.assignedUser.name
                        : "Unassigned"}
                      {canManageTask && " ✏️"}
                    </span>
                  )}
                </div>

                <div className="info-item">
                  <span className="info-label">Created By</span>
                  <span>
                    {selectedTask?.createdBy
                      ? selectedTask?.createdBy.name
                      : "Unknown"}
                  </span>
                </div>

                <div className="info-item">
                  <span className="info-label">Type</span>
                  {/* {editMode.type ? (
                                        <div className="edit-field-container">
                                            <input
                                                type="text"
                                                className="edit-field-input"
                                                value={editedValues.type || ''}
                                                onChange={(e) => handleEditChange('type', e.target.value)}
                                            />
                                            <div className="edit-field-actions">
                                                <button
                                                    className="save-edit-button"
                                                    onClick={() => saveFieldChange('type')}
                                                    disabled={isUpdating}
                                                >
                                                    {isUpdating ? '...' : '✓'}
                                                </button>
                                                <button
                                                    className="cancel-edit-button"
                                                    onClick={() => cancelEdit('type')}
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                    ) : ( */}
                  <span onClick={() => enableEditMode("type")}>
                    {selectedTask?.type ? selectedTask?.type : "--"}
                    {/* ✏️ */}
                  </span>
                  {/* )} */}
                </div>
              </div>
            </div>
          )}

          {activeTab === "files" && (
            <div className="files-tab">
              <div className="files-header">
                <h3>Task Attachments</h3>
                {canManageTask && (
                  <div className="file-upload-container">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      style={{ display: "none" }}
                    />
                    <button
                      className="upload-button"
                      onClick={() => fileInputRef.current.click()}
                    >
                      Upload Files
                    </button>
                  </div>
                )}
              </div>

              {selectedTask.attachments &&
                selectedTask.attachments.length > 0 ? (
                <div className="existing-files">
                  <h4>Existing Files ({selectedTask.attachments.length})</h4>
                  <div className="tm-att-grid">
                    {selectedTask.attachments.map((file, index) => (
                      <AttachmentCard
                        key={(typeof file === "object" && file._id) || index}
                        taskId={selectedTask._id}
                        file={file}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="no-files-message">
                  <div className="no-files-icon">📂</div>
                  <p>No files attached to this task yet</p>
                </div>
              )}

              {canManageTask && files.length > 0 && (
                <div className="new-files">
                  <h4>New Files to Upload</h4>
                  <div className="file-preview-list">
                    {files.map((file, index) => (
                      <div key={index} className="file-preview-container">
                        <FilePreview file={file} />
                        <button
                          className="remove-file-button"
                          onClick={() => removeFile(index)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    className="upload-all-button"
                    onClick={handleUploadFiles}
                    disabled={isUpdating}
                  >
                    {isUpdating ? "Uploading..." : "Upload All Files"}
                  </button>
                </div>
              )}
            </div>
          )}

          {canManageTask && showDeleteModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <div className="modal-header">
                  <div className="modal-icon">⚠️</div>
                  <h3 className="modal-title">Confirm Deletion</h3>
                </div>
                <div className="modal-body">
                  <p>
                    Are you sure you want to delete the attachment{" "}
                    <strong>{selectedTask.fileName}</strong>?
                  </p>
                  <p>This action cannot be undone.</p>
                </div>
                <div className="modal-actions">
                  <button
                    className="delete-confirm-button"
                    onClick={() => deleteAttachments()}
                  >
                    Yes, Delete Attachments
                  </button>
                  <button
                    className="cancel-button"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>

        <div className="modal-footer">
          <div className="status-panel">
          <div className="status-actions" role="group" aria-label="Task status">
            {[
              { value: "pending", label: "Waiting to Start", className: "pending" },
              { value: "in progress", label: "Working On It", className: "in-progress" },
              { value: "completed", label: "All Done!", className: "completed" },
            ].map((opt) => {
              const isCurrent = currentStatus === opt.value;
              const blocked = opt.value === "completed" && openSubtasks > 0 && !isCurrent;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={`status-button ${opt.className}${isCurrent ? " active" : ""}${blocked ? " is-blocked" : ""}`}
                  aria-pressed={isCurrent}
                  onClick={() => handleStatusChange(opt.value)}
                  disabled={isUpdating || !canManageTask}
                  title={isCurrent ? "Current status" : blocked ? "Complete all sub-tasks first" : `Change status to "${opt.label}"`}
                >
                  {isUpdating && !isCurrent ? "Updating..." : `${isCurrent ? "✓ " : ""}${opt.label}`}
                  {isCurrent && <small className="status-button__now">(current)</small>}
                </button>
              );
            })}
          </div>
          {canManageTask && openSubtasks > 0 && currentStatus !== "completed" && (
            <p className="status-hint">
              {openSubtasks} sub-task{openSubtasks > 1 ? "s are" : " is"} still open — finish {openSubtasks > 1 ? "them" : "it"} to mark this task All Done.
            </p>
          )}
          </div>
        </div>

        <style jsx>{`
          /* Existing styles */
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 1000;
          }

          .task-details-modal {
            background-color: white;
            border-radius: 8px;
            width: 90%;
            max-width: 800px;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 16px 20px;
            border-bottom: 1px solid #eee;
          }

          .modal-header h2 {
            margin: 0;
            cursor: pointer;
          }

          .modal-header h2:hover {
            text-decoration: underline;
          }

          .close-button {
            background: none;
            border: none;
            font-size: 24px;
            cursor: pointer;
          }

          .modal-tabs {
            display: flex;
            border-bottom: 1px solid #eee;
          }

          .tab-button {
            padding: 12px 20px;
            background: none;
            border: none;
            cursor: pointer;
            font-size: 16px;
            opacity: 0.7;
            border-bottom: 3px solid transparent;
          }

          .tab-button.active {
            opacity: 1;
            border-bottom-color: #3498db;
            font-weight: bold;
          }

          .modal-body {
            padding: 20px;
          }

          .task-info {
            display: flex;
            flex-direction: column;
            gap: 20px;
          }

          .info-section h3 {
            margin-bottom: 10px;
            font-size: 18px;
          }

          .info-section p {
            margin: 0;
            cursor: pointer;
          }

          .info-section p:hover {
            text-decoration: underline;
          }

          .info-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
            gap: 16px;
          }

          .info-item {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .info-label {
            font-size: 14px;
            color: #666;
          }

          .info-item span:not(.info-label) {
            cursor: pointer;
          }

          .info-item span:not(.info-label):hover {
            text-decoration: underline;
          }

          .status-badge,
          .priority-badge {
            display: inline-block;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 14px;
          }

          .status-badge.status-pending {
            background-color: #f39c12;
            color: white;
          }

          .status-badge.status-in-progress {
            background-color: #3498db;
            color: white;
          }

          .status-badge.status-completed {
            background-color: #2ecc71;
            color: white;
          }

          .priority-badge.priority-high {
            background-color: #ff7979;
            color: white;
          }

          .priority-badge.priority-medium {
            background-color: #f6e58d;
          }

          .priority-badge.priority-low {
            background-color: #badc58;
          }

          .deadline-text.overdue {
            color: #e74c3c;
            font-weight: bold;
          }

          .files-tab,
          .comments-tab {
            display: flex;
            flex-direction: column;
            gap: 20px;
          }

          .files-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .upload-button,
          .attach-button,
          .post-comment-button,
          .upload-all-button {
            padding: 8px 16px;
            border-radius: 4px;
            border: none;
            cursor: pointer;
            font-size: 14px;
            background-color: #3498db;
            color: white;
          }

          .file-list {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }

          .file-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px;
            border-radius: 4px;
            background-color: #f9f9f9;
          }

          .file-icon {
            font-size: 24px;
          }

          .file-details {
            flex: 1;
          }

          .file-name {
            font-weight: bold;
            margin-bottom: 4px;
          }

          .file-meta {
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 14px;
            color: #666;
          }

          .download-wrapper {
            display: flex;
            gap: 12px;
          }

          .file-download {
            color: #3498db;
            cursor: pointer;
            text-decoration: none;
          }

          .file-download-delete {
            color: #e74c3c;
          }

          .no-files-message,
          .no-comments-message {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 40px;
            color: #666;
            text-align: center;
          }

          .no-files-icon,
          .no-comments-icon {
            font-size: 48px;
            margin-bottom: 16px;
            opacity: 0.5;
          }

          .file-preview-list {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
          }

          .file-preview-container {
            position: relative;
            padding: 12px;
            border-radius: 4px;
            background-color: #f9f9f9;
          }

          .remove-file-button {
            position: absolute;
            top: 4px;
            right: 4px;
            background: #e74c3c;
            color: white;
            border: none;
            border-radius: 50%;
            width: 20px;
            height: 20px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
          }

          .comments-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          .comment {
            padding: 12px;
            border-radius: 4px;
            background-color: #f9f9f9;
          }

          .comment-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 8px;
          }

          .comment-user {
            font-weight: bold;
          }

          .comment-date {
            font-size: 12px;
            color: #666;
          }

          .delete-comment {
            background: none;
            border: none;
            cursor: pointer;
            color: #e74c3c;
            font-size: 16px;
          }

          .comment-text {
            margin-bottom: 8px;
          }

          .comment-attachments {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }

          .comment-attachment {
            padding: 4px 8px;
            background-color: #eee;
            border-radius: 4px;
            font-size: 12px;
            text-decoration: none;
            color: #333;
          }

          .add-comment {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }

          .comment-input {
            padding: 12px;
            border-radius: 4px;
            border: 1px solid #ddd;
            resize: vertical;
            min-height: 80px;
          }

          .comment-actions {
            display: flex;
            justify-content: space-between;
          }

          .comment-attachments-preview {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }

          .attachment-preview {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 4px 8px;
            background-color: #eee;
            border-radius: 4px;
            font-size: 12px;
          }

          .remove-attachment {
            background: none;
            border: none;
            cursor: pointer;
            color: #e74c3c;
          }

          .modal-footer {
            padding: 16px 20px;
            border-top: 1px solid #eee;
          }

          .status-actions {
            display: flex;
            justify-content: space-between;
            gap: 12px;
          }

          /* New styles for editable fields */
          .edit-field-container {
            position: relative;
            width: 100%;
          }

          .edit-field-input,
          .edit-field-textarea,
          .edit-field-select,
          .edit-field-date {
            width: 100%;
            padding: 8px;
            border: 1px solid #3498db;
            border-radius: 4px;
            font-size: 14px;
            background-color: #f8f9fa;
          }

          .edit-field-textarea {
            min-height: 100px;
            resize: vertical;
          }

          .edit-field-actions {
            position: absolute;
            top: 8px;
            right: 8px;
            display: flex;
            gap: 4px;
          }

          .save-edit-button,
          .cancel-edit-button {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            border: none;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 12px;
          }

          .save-edit-button {
            background-color: #2ecc71;
            color: white;
          }

          .save-edit-button:disabled {
            background-color: #95a5a6;
            cursor: not-allowed;
          }

          .cancel-edit-button {
            background-color: #e74c3c;
            color: white;
          }

          .modal-content {
            background-color: white;
            border-radius: 8px;
            width: 90%;
            max-width: 500px;
            padding: 20px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          }

          .modal-icon {
            font-size: 24px;
            margin-right: 10px;
          }

          .modal-title {
            margin: 0;
          }

          .modal-actions {
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            margin-top: 20px;
          }

          .delete-confirm-button {
            background-color: #e74c3c;
            color: white;
            border: none;
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
          }

          .cancel-button {
            background-color: #95a5a6;
            color: white;
            border: none;
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
          }
        `}</style>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Task Tracker (list view)
// ---------------------------------------------------------------------------
const PAGE_SIZE = 8;
// The list endpoint pages server-side (10 per page by default). The tracker needs counts across
// every task for its Urgent / Important / Completed chips plus free-text search, so it loads the
// whole (filtered) list in one request and pages in the browser.
const LIST_LIMIT = 1000;

const ROLE_LABELS = {
  admin: "Admin",
  coach: "Coach",
  "sports-coach": "Sports Coach",
  "music-coach": "Music Coach",
  "purchase-manager": "Purchase Manager",
  "medical-incharge": "Medical Incharge",
  "balagruha-incharge": "Balagruha Incharge",
  amma: "Amma",
  student: "Student",
};
const roleLabel = (role) =>
  ROLE_LABELS[(role || "").toLowerCase()] ||
  (role ? role.split("-").map(capitalize).join(" ") : "");

const taskStatus = (task) => (task.status || "").toLowerCase();
const taskPriority = (task) => (task.priority || "").toLowerCase();
const isCompleted = (task) => taskStatus(task) === "completed";
// Chip buckets: Urgent = open high-priority, Important = open medium-priority.
const CHIPS = [
  { id: "all", label: "All Tasks", match: () => true },
  { id: "urgent", label: "Urgent", match: (t) => !isCompleted(t) && taskPriority(t) === "high" },
  { id: "important", label: "Important", match: (t) => !isCompleted(t) && taskPriority(t) === "medium" },
  { id: "completed", label: "Completed", match: isCompleted },
];
const dotClass = (task) =>
  isCompleted(task) ? "is-done" : `is-${taskPriority(task) || "low"}`;

const formatTaskDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getDate()} ${d.toLocaleDateString("en-GB", { month: "short" })} ${d.getFullYear()}`;
};

const ChipIcon = ({ id }) => {
  const props = { width: 15, height: 15, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true };
  if (id === "urgent") return <svg {...props}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>;
  if (id === "important") return <svg {...props}><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>;
  if (id === "completed") return <svg {...props}><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></svg>;
  return <svg {...props}><path d="M3 6h18" /><path d="M7 12h10" /><path d="M10 18h4" /></svg>;
};

// Same id extraction as the coach Task Tracker (create responses vary in shape).
const extractCreatedTaskId = (response) => {
  if (!response) return undefined;
  if (Array.isArray(response)) return response[0]?._id;
  if (response._id) return response._id;
  const task = response.data?.task ?? response.task;
  if (Array.isArray(task)) return task[0]?._id;
  return task?._id;
};

// Pages to show around the current one, e.g. 1 … 4 5 6 … 12.
const pageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  return sorted.flatMap((p, i) => (i > 0 && p - sorted[i - 1] > 1 ? ["…", p] : [p]));
};

// Main Task Management Component
const TaskManagement = () => {
  const [tasks, setTasks] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [users, setUsers] = useState([]);
  const [balagruhas, setBalagruhas] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetails, setShowTaskDetails] = useState(false);
  const [detailsTab, setDetailsTab] = useState("details");
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [filters, setFilters] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [assignableStaff, setAssignableStaff] = useState([]);
  const [chip, setChip] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [reassigning, setReassigning] = useState("");
  const [chatTask, setChatTask] = useState(null);
  const role = localStorage.getItem("role");
  const normalizedRole = (role || "").toLowerCase();
  const isStudent = normalizedRole === "student";

  // Fetch balagruha list. Admins get every Balgruha; other roles get their own.
  const getBalagruhaList = async () => {
    try {
      let balagruhaList;
      if (normalizedRole === "admin") {
        const response = await getBalagruha();
        const list = response?.data?.balagruhas || response?.data;
        balagruhaList = Array.isArray(list) ? list : [];
      } else {
        const id = localStorage.getItem("userId");
        const response = await getBalagruhaById(id);
        balagruhaList = response?.data?.balagruhas || [];
      }
      setBalagruhas(balagruhaList);
    } catch (error) {
      console.error("Error fetching balagruhas:", error);
      addToast("Failed to load balagruhas", "error");
    }
  };

  const getAllTasks = async (filterParams = {}) => {
    setIsLoading(true);
    const finalFilters = { ...filterParams, page: 1, limit: LIST_LIMIT };

    // Sports-coach and Music-coach are scoped to their own task type by
    // design (they also create into separate sports/music collections).
    if (role === "sports-coach") {
      finalFilters.type = ["sports"];
    } else if (role === "music-coach") {
      finalFilters.type = ["music"];
    }
    try {
      const response = await getTasks(JSON.stringify(finalFilters));
      setTasks(response?.data?.tasks || []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      addToast("Failed to load tasks", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = async (newFilters) => {
    setFilters(newFilters);
    setPage(1);
    await getAllTasks(newFilters);
  };

  const handleAddTask = async (taskData) => {
    try {
      if (isStudent) {
        addToast("Students cannot create tasks", "error");
        return;
      }
      const formData = new FormData();
      formData.append("title", taskData.title);
      formData.append("description", taskData.description);
      const assignedUserIds = Array.isArray(taskData.assignedUser)
        ? taskData.assignedUser.join(",")
        : taskData.assignedUser || "";
      formData.append("assignedUser", assignedUserIds);
      formData.append("createdBy", localStorage.getItem("userId"));
      formData.append("deadline", taskData.deadline);
      formData.append("priority", taskData.priority);
      formData.append("status", taskData.status);

      if (taskData.balagruhaId) {
        formData.append("balagruhaId", taskData.balagruhaId);
      }

      if (
        localStorage.getItem("role") === "sports-coach" ||
        localStorage.getItem("role") === "music-coach"
      ) {
        formData.append("drillOrExerciseType", taskData.drillOrExerciseType);
      }

      if (taskData.type === "purchase" || taskData.type === "order") {
        formData.append("machineDetails", taskData.machineDetails);
        formData.append("vendorDetails", taskData.vendorDetails);
        formData.append("costEstimate", taskData.costEstimate);
        formData.append("type", taskData.type);
      }

      if (taskData.type === "order") {
        formData.append("repairDetails", taskData.repairDetails);
      } else if (taskData.type === "purchase") {
        formData.append("requiredParts", taskData.requiredParts);
      }

      // Handle medical tasks
      if (taskData.type === "medical") {
        formData.append("type", taskData.type);
        if (taskData.balagruhaId) {
          formData.append("balagruhaId", taskData.balagruhaId);
        }
        if (taskData.students && taskData.students.length > 0) {
          taskData.students.forEach((studentId) => {
            formData.append("students[]", studentId);
          });
        }
      }

      if (taskData.attachments && taskData.attachments.length > 0) {
        taskData.attachments.forEach((file) => {
          formData.append("attachments", file);
        });
      }

      let createResponse;
      if (localStorage.getItem("role") === "sports-coach") {
        createResponse = await createSportsTask(formData);
      } else if (localStorage.getItem("role") === "music-coach") {
        createResponse = await createMusicTask(formData);
      } else {
        createResponse = await createTask(formData);
      }

      const createdTaskId = extractCreatedTaskId(createResponse);
      if (createdTaskId && Array.isArray(taskData.subtasks) && taskData.subtasks.length > 0) {
        try {
          // One at a time: the backend rewrites the whole subtasks array on each add, so
          // parallel calls would overwrite each other (see TaskTracker.js).
          for (const subtask of taskData.subtasks) {
            await addSubtask(createdTaskId, {
              title: subtask.title,
              dueDate: subtask.dueDate || undefined,
              priority: subtask.priority || undefined,
            });
          }
        } catch (subtaskError) {
          addToast("Task created, but some sub-tasks failed to save", "error");
        }
      }

      addToast(`Task "${taskData.title}" created successfully!`, "success");
      setShowCreateTask(false);
      getAllTasks(filters);
    } catch (error) {
      console.log("Status:", error.response?.status);
      console.log("Backend Response:", error.response?.data);
      console.log("Payload Sent:", taskData);

      addToast(error?.response?.data?.message || "Failed to create task", "error");
    }
  };

  const getUsers = async () => {
    try {
      const response = await fetchUsers();
      setUsers(Array.isArray(response) ? response : (response?.data || []));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const getAssignableUsersList = async () => {
    try {
      if (isStudent) {
        setAssignableStaff([]);
        return;
      }
      const response = await getAssignableTaskUsers();
      setAssignableStaff(response?.data?.staff || []);
    } catch (error) {
      console.error("Error fetching assignable users:", error);
      addToast("Failed to load assignable users", "error");
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await getAssignableUsersList();
      await getUsers();
      await getBalagruhaList();
    };

    initializeData();
  }, []);

  // Effect to load tasks when filters change or when balagruhas are loaded
  useEffect(() => {
    if (balagruhas.length > 0) {
      getAllTasks(filters);
    } else {
      setIsLoading(false);
    }
  }, [balagruhas]);

  const addToast = (message, type = "info") => {
    const newToast = {
      id: Date.now(),
      message,
      type,
    };
    setToasts((prevToasts) => [...prevToasts, newToast]);
  };

  const removeToast = (id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  const getTaskDetailsByTaskId = async (id) => {
    try {
      const response = await getTaskBytaskId(id);
      setSelectedTask(response);
    } catch (err) {
      console.error("Error updating task status:", err);
      addToast("Failed to fetch Task based on id", "error");
    }
  };

  const updateTaskStatus = async (id, status) => {
    const statusMessages = {
      pending: 'Task moved to "Waiting to Start"',
      "in progress": 'Task moved to "Working On It"',
      completed: 'Task marked as "All Done!"',
    };

    try {
      if (isStudent) {
        addToast("Students cannot update tasks", "error");
        return;
      }

      if (role === "sports-coach") {
        await updateSportsTask(id, JSON.stringify({ status }));
      } else if (role === "music-coach") {
        await updateMusicTask(id, JSON.stringify({ status }));
      } else {
        await updateTask(id, JSON.stringify({ status }));
      }

      setTasks((prevTasks) =>
        prevTasks.map((task) => (task._id === id ? { ...task, status } : task))
      );
      setSelectedTask((prev) =>
        prev && prev._id === id ? { ...prev, status } : prev
      );

      resultDialog("success", statusMessages[status]);
      await getAllTasks(filters);
      await getTaskDetailsByTaskId(id);
    } catch (error) {
      console.error("Error updating task status:", error);
      resultDialog("error", "Could not update the status", error?.response?.data?.message || "Please try again.");
    }
  };

  // Update task with new data
  const handleUpdateTask = async (id, updateData) => {
    try {
      if (isStudent) {
        addToast("Students cannot update tasks", "error");
        return;
      }
      const response = await updateTask(id, JSON.stringify(updateData));
      const updatedTask =
        response?.task || response?.data?.task || response?.data || response;

      addToast("Task updated successfully", "success");

      getAllTasks(filters);
      getTaskDetailsByTaskId(id);
      if (updatedTask && updatedTask._id === id) {
        setSelectedTask(updatedTask);
      }
    } catch (error) {
      console.error("Error updating task:", error);
      addToast("Failed to update task", "error");
    }
  };

  const openTask = (task, tab = "details") => {
    setDetailsTab(tab);
    setSelectedTask(task);
    setShowTaskDetails(true);
  };

  // Staff the task can be reassigned to; keeps the current assignee listed even if they
  // are not in the assignable list (e.g. a student or an inactive user).
  const assigneeOptions = (task) => {
    const current = task.assignedUser;
    const list = assignableStaff.map((u) => ({ id: u._id || u.id, name: u.name, role: u.role }));
    if (current?._id && !list.some((u) => u.id === current._id)) {
      list.unshift({ id: current._id, name: current.name, role: current.role });
    }
    return list;
  };

  const reassignTask = async (task, newUserId) => {
    const current = task.assignedUser || {};
    if (!newUserId || newUserId === current._id) return;
    const target = assigneeOptions(task).find((u) => u.id === newUserId);
    const confirmed = await confirmDialog({
      title: "Reassign this task?",
      html: `<b>${escapeHtml(task.title)}</b><br/>From <b>${escapeHtml(current.name || "—")}</b> to <b>${escapeHtml(target?.name || "—")}</b>${target?.role ? ` (${escapeHtml(roleLabel(target.role))})` : ""}`,
      confirmText: "Yes, reassign",
      cancelText: "Cancel",
    });
    if (!confirmed) return;
    setReassigning(task._id);
    try {
      await updateTask(task._id, JSON.stringify({ assignedUser: newUserId }));
      resultDialog("success", "Task reassigned", `Now assigned to ${target?.name || "the selected user"}.`);
      await getAllTasks(filters);
    } catch (error) {
      resultDialog("error", "Could not reassign the task", error?.response?.data?.message || "Please try again.");
    } finally {
      setReassigning("");
    }
  };

  const counts = useMemo(
    () => Object.fromEntries(CHIPS.map((c) => [c.id, tasks.filter(c.match).length])),
    [tasks]
  );
  const visibleTasks = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matchChip = CHIPS.find((c) => c.id === chip)?.match || (() => true);
    return tasks.filter((t) => {
      if (!matchChip(t)) return false;
      if (!q) return true;
      return [t.title, t.description, t.assignedUser?.name, t.createdBy?.name]
        .some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [tasks, chip, search]);
  const totalPages = Math.max(1, Math.ceil(visibleTasks.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageTasks = visibleTasks.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const firstShown = visibleTasks.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const lastShown = Math.min(currentPage * PAGE_SIZE, visibleTasks.length);

  const selectedBalagruha = filters.balagruhaId?.[0] || "";
  // Assignees for the Add Task form, labelled with their role since admins can assign any staff.
  const assigneeChoices = useMemo(
    () => assignableStaff.map((u) => ({ _id: u._id || u.id, name: u.role ? `${u.name} (${roleLabel(u.role)})` : u.name })),
    [assignableStaff]
  );
  const selectedAssignee = filters.assignedFor?.[0] || "";

  return (
    <div className="tt-page">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {showTaskDetails && selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          initialTab={detailsTab}
          onClose={async () => {
            setShowTaskDetails(false);
            await getAllTasks(filters);
          }}
          users={users}
          onStatusChange={updateTaskStatus}
          onUpdateTask={handleUpdateTask}
        />
      )}

      {showCreateTask && (
        // Same "Add New Task" form the coach Task Tracker uses.
        <AddTaskModal
          users={assigneeChoices}
          balagruhas={balagruhas}
          defaultBalagruhaId={selectedBalagruha}
          onSubmit={handleAddTask}
          onCancel={() => setShowCreateTask(false)}
        />
      )}

      {/* Same chat panel as the coach Task Tracker */}
      {chatTask && (
        <TaskChatPanel
          key={chatTask._id}
          task={chatTask}
          onClose={() => setChatTask(null)}
          onMessageSent={() => getAllTasks(filters)}
          onMentionsSeen={(taskId) => setTasks((list) => withMentionsSeen(list, taskId))}
        />
      )}

      <header className="tt-header">
        <div className="tt-title">
          <span className="tt-title__icon" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="m9 14 2 2 4-4" /></svg>
          </span>
          <div>
            <h1>Task Tracker</h1>
            <p>Plan · Assign · Track · Get Things Done</p>
          </div>
        </div>
        <div className="tt-header__actions">
          <label className="tt-select">
            <span>Balgruha</span>
            <SearchableSelect
              searchPlaceholder="Search balagruha..."
              value={selectedBalagruha}
              onChange={(e) => handleFilterChange({ ...filters, balagruhaId: e.target.value ? [e.target.value] : [] })}
            >
              <option value="">All Balgruhas</option>
              {balagruhas.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
            </SearchableSelect>
          </label>
          {!isStudent && (
            <label className="tt-select">
              <span>Assignee</span>
              <SearchableSelect
                searchPlaceholder="Search coach..."
                value={selectedAssignee}
                onChange={(e) => handleFilterChange({ ...filters, assignedFor: e.target.value ? [e.target.value] : [] })}
              >
                <option value="">All Coaches</option>
                {assignableStaff.map((u) => (
                  <option key={u._id || u.id} value={u._id || u.id}>{u.name}{u.role ? ` (${roleLabel(u.role)})` : ""}</option>
                ))}
              </SearchableSelect>
            </label>
          )}
          {!isStudent && (
            <button type="button" className="tt-add" onClick={() => setShowCreateTask(true)}>
              <span aria-hidden="true">+</span> Add Task
            </button>
          )}
        </div>
      </header>

      <div className="tt-toolbar">
        <div className="tt-chips" role="tablist">
          {CHIPS.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={chip === c.id}
              className={`tt-chip tt-chip--${c.id}${chip === c.id ? " is-active" : ""}`}
              onClick={() => { setChip(c.id); setPage(1); }}
            >
              <ChipIcon id={c.id} />
              {c.label}{c.id !== "all" ? ` (${counts[c.id]})` : ""}
            </button>
          ))}
        </div>
        <label className="tt-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
          <input
            type="search"
            value={search}
            placeholder="Search tasks..."
            aria-label="Search tasks"
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </label>
      </div>

      <section className="tt-card">
        <div className="tt-table-wrap">
          <table className="tt-table">
            <thead>
              <tr>
                <th>Sr No</th>
                <th>Assignor</th>
                <th>Assignee</th>
                <th>Task Name</th>
                <th>Assigned Date</th>
                <th>Due Date</th>
                <th>Chat</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={8} className="tt-empty">Loading tasks…</td></tr>
              ) : !pageTasks.length ? (
                <tr><td colSpan={8} className="tt-empty">{tasks.length ? "No tasks match these filters." : "No tasks yet."}</td></tr>
              ) : pageTasks.map((task, i) => {
                const subtasks = (task.subtasks || []).length;
                const chats = (task.comments || []).length;
                const options = assigneeOptions(task);
                return (
                  <tr key={task._id}>
                    <td>{firstShown + i}</td>
                    <td>
                      <div className="tt-person">
                        <b>{task.createdBy?.name || "—"}</b>
                        {task.createdBy?.role && <small>({roleLabel(task.createdBy.role)})</small>}
                      </div>
                    </td>
                    <td>
                      {isStudent || !options.length ? (
                        <div className="tt-person tt-person--blue">
                          <b>{task.assignedUser?.name || "—"}</b>
                          {task.assignedUser?.role && <small>({roleLabel(task.assignedUser.role)})</small>}
                        </div>
                      ) : (
                        <label className={`tt-assignee${reassigning === task._id ? " is-busy" : ""}`}>
                          <span className="tt-person tt-person--blue" aria-hidden="true">
                            <b>{task.assignedUser?.name || "Unassigned"}</b>
                            {task.assignedUser?.role && <small>({roleLabel(task.assignedUser.role)})</small>}
                          </span>
                          <svg className="tt-assignee__chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
                          <select
                            value={task.assignedUser?._id || ""}
                            disabled={reassigning === task._id}
                            aria-label={`Assignee for ${task.title}`}
                            onChange={(e) => reassignTask(task, e.target.value)}
                          >
                            {options.map((u) => (
                              <option key={u.id} value={u.id}>{u.name}{u.role ? ` (${roleLabel(u.role)})` : ""}</option>
                            ))}
                          </select>
                        </label>
                      )}
                    </td>
                    <td>
                      <button type="button" className="tt-task" onClick={() => openTask(task)}>
                        <i className={`tt-dot ${dotClass(task)}`} title={isCompleted(task) ? "Completed" : `${capitalize(taskPriority(task) || "low")} priority`} />
                        <span>{task.title}</span>
                      </button>
                      {subtasks > 0 && <small className="tt-sub">+ {subtasks} Sub-task{subtasks > 1 ? "s" : ""}</small>}
                    </td>
                    <td>{formatTaskDate(task.createdAt)}</td>
                    <td className={!isCompleted(task) && task.deadline && new Date(task.deadline) < new Date(new Date().toDateString()) ? "tt-overdue" : ""}>
                      {formatTaskDate(task.deadline)}
                    </td>
                    <td>
                      <div className="tt-chat">
                        <span title={task.description}>{task.description || ""}</span>
                        <button type="button" onClick={() => setChatTask(task)}>
                          View Chat{chats ? ` (${chats})` : ""}
                          {hasUnseenMention(task) && <MentionDot />}
                        </button>
                      </div>
                    </td>
                    <td>
                      <button type="button" className="tt-edit" aria-label={`Open ${task.title}`} title={isStudent ? "View task" : "View / edit task"} onClick={() => openTask(task)}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21.17 6.81a1 1 0 0 0-3.98-3.98L3.84 16.17a2 2 0 0 0-.5.83l-1.32 4.35a.5.5 0 0 0 .62.62l4.35-1.32a2 2 0 0 0 .83-.5z" /></svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <footer className="tt-footer">
          <span>
            {visibleTasks.length
              ? `Showing ${firstShown} to ${lastShown} of ${visibleTasks.length} task${visibleTasks.length > 1 ? "s" : ""}`
              : "Showing 0 tasks"}
          </span>
          {totalPages > 1 && (
            <nav className="tt-pages" aria-label="Task pages">
              <button type="button" aria-label="Previous page" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>‹</button>
              {pageWindow(currentPage, totalPages).map((p, i) => (p === "…"
                ? <span key={`gap-${i}`}>…</span>
                : <button key={p} type="button" className={p === currentPage ? "is-active" : ""} aria-current={p === currentPage ? "page" : undefined} onClick={() => setPage(p)}>{p}</button>))}
              <button type="button" aria-label="Next page" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}>›</button>
            </nav>
          )}
        </footer>
      </section>
    </div>
  );
};

export default TaskManagement;
